import { useAuth } from 'src/auth/AuthContext';
import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Snackbar
} from '@mui/material';
import {
  Info as InfoIcon,
  QuestionMark as QuestionMarkIcon,
  CardMembership,
  PriceCheck,
  AdminPanelSettings,
  Send,
  LocalOffer as LocalOfferIcon
} from '@mui/icons-material';
import { useCurrentLocation } from 'src/components/Routes/LocationRoute';
import { DigitalFlyer } from '../../services/digitalFlyersApi';
import {
  useCreateFlyer,
  useEditFlyer,
  useMerchantFlyers,
  useSendFlyer,
} from 'src/queries/flyers';
import { useFavouriteUsers } from 'src/queries/loyalty';
import { initiatePayfastCheckout } from '../../utils/payfast';
import { usePricing } from '../../hooks/usePricing';

const DigitalFlyersPage: React.FC = () => { 
  const user = useAuth();
  const { merchant } = useCurrentLocation();
  const [message, setMessage] = useState('');
  const [quote, setQuote] = useState<number | null>(null);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = useState(false);
  const [flyerToSend, setFlyerToSend] = useState<DigitalFlyer | null>(null);
  const [selectedFlyer, setSelectedFlyer] = useState<DigitalFlyer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  // Flyer unit pricing (ZAR) + display FX come from the backend pricing
  // endpoint; payment amounts are resolved server-side at checkout.
  const { pricing } = usePricing(merchant.address?.countryCode);

  // Prices are charged in ZAR; display in local currency when FX is
  // available, otherwise fall back to showing ZAR.
  const zarPerUnit = pricing?.zar_per_unit ?? null;
  const displaySymbol = pricing && zarPerUnit ? pricing.symbol : 'R';
  const displayCurrency = pricing && zarPerUnit ? pricing.currency : 'ZAR';
  const toDisplayCurrency = (zarAmount: number) =>
    zarPerUnit ? zarAmount / zarPerUnit : zarAmount;

  const localCurrencyCostPerUser =
    pricing ? toDisplayCurrency(pricing.flyer_zar_cost_per_user) : null;
  const localCurrencyMinimumCost =
    pricing ? toDisplayCurrency(pricing.flyer_zar_minimum_cost) : null;

  const calculateZarQuote = (): number | null => {
    if (!pricing) return null;
    const numberOfUsers = favoriteUsersCount || 0;
    return Math.max(numberOfUsers * pricing.flyer_zar_cost_per_user, pricing.flyer_zar_minimum_cost);
  };

  /**
   * These two ran sequentially inside one effect whose dependency array included
   * `axios.defaults.headers.common.Authorization` — a mutable global, compared by
   * reference, so it could never trigger a re-run. Readiness is now the queries'
   * `enabled`, and the two requests are independent.
   *
   * The favourite-users count is shared with the analytics dashboard, so the two
   * pages hit one cache entry rather than one request each.
   */
  const {
    data: flyers = [],
    isPending: flyersPending,
    isError: flyersError,
  } = useMerchantFlyers(merchant?.id);
  const { data: favouriteUsers } = useFavouriteUsers(merchant?.id);

  const favoriteUsersCount = favouriteUsers?.length ?? null;

  const createFlyer = useCreateFlyer();
  const editFlyer = useEditFlyer();
  const sendFlyer = useSendFlyer();

  const isLoading = isSubmitting || createFlyer.isPending || editFlyer.isPending;
  const isSending = sendFlyer.isPending;

  /**
   * Derived rather than stored. This used to be state that every write path had
   * to remember to recompute via checkInProgressFlyers(), which is the kind of
   * bookkeeping that goes stale the moment one path forgets.
   */
  const hasInProgressFlyer = useMemo(
    () =>
      flyers.some((flyer) =>
        ['AWAITING_APPROVAL', 'REJECTED', 'READY_TO_SEND'].includes(flyer.status)
      ),
    [flyers]
  );

  const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMessage = event.target.value;
    if (newMessage.length <= 100) {
      setMessage(newMessage);
      setError(null);
    }
  };

  const generateQuote = async () => {
    if (message.trim() === '') {
      setError('Please enter a message');
      return;
    }

    if (!merchant.id) {
      setError('No location selected');
      return;
    }

    if (hasInProgressFlyer) {
      setError('You already have a flyer in progress. Please wait for it to be completed before creating a new one.');
      return;
    }

    setIsSubmitting(true);
    try {
      const zarQuote = calculateZarQuote();
      if (zarQuote === null) {
        setError('Pricing is unavailable right now. Please try again shortly.');
        return;
      }

      // Charged in ZAR; shown in the local currency where FX is available.
      setQuote(toDisplayCurrency(zarQuote));
      setShowQuoteDialog(true);
    } catch (err) {
      setError('Failed to generate quote. Please try again.');
      console.error('Error generating quote:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!merchant.id || !quote) return;

    setIsSubmitting(true);
    try {
      // Check if merchant has free flyers available
      const hasFreeFlyers = merchant.freeFlyersRemaining > 0;
      
      if (hasFreeFlyers) {
        await createFlyer.mutateAsync({
          locationId: merchant.id,
          userId: user.id,
          message,
          cost: 0,
          isFree: true,
        });
        // No explicit refetch: the mutation invalidates this location's key and
        // freeFlyersRemaining comes back with the record.
        setShowQuoteDialog(false);
        setMessage('');
      } else {
        const zarQuote = calculateZarQuote();
        if (zarQuote === null) {
          setError('Pricing is unavailable right now. Please try again shortly.');
          return;
        }

        const newFlyer = await createFlyer.mutateAsync({
          locationId: merchant.id,
          userId: user.id,
          message,
          cost: zarQuote,
        });
        setShowQuoteDialog(false);
        setMessage('');
        // The charge amount is read from the flyer record server-side.
        await initiatePayfastCheckout({ merchantId: merchant.id, intent: 'digitalFlyer', flyerId: newFlyer.id });
      }
    } catch (err) {
      setError('Failed to create flyer. Please try again.');
      console.error('Error creating flyer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditFlyer = (flyer: DigitalFlyer) => {
    setSelectedFlyer(flyer);
    setMessage(flyer.message);
    setShowEditDialog(true);
  };

  const handleUpdateFlyer = async () => {
    if (!selectedFlyer) return;

    setIsSubmitting(true);
    try {
      await editFlyer.mutateAsync({ flyerId: selectedFlyer.id, message });
      setShowEditDialog(false);
      setMessage('');
      setSelectedFlyer(null);
    } catch (err) {
      setError('Failed to update flyer. Please try again.');
      console.error('Error updating flyer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'READY_TO_SEND':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'AWAITING_APPROVAL':
        return 'warning';
      case 'PENDING':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleSendFlyerClick = (flyer: DigitalFlyer) => {
    setFlyerToSend(flyer);
    setShowSendConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    if (!flyerToSend) return;

    // No local submitting flag here: `sendFlyer.isPending` already drives the
    // Send button, and setting isSubmitting would also spin the unrelated
    // quote button.
    try {
      await sendFlyer.mutateAsync({
        flyerId: flyerToSend.id,
        locationId: merchant.id,
      });
      setShowSendConfirmDialog(false);
      setFlyerToSend(null);
      setSuccessMessage('Digital flyer sent successfully! Your customers will receive a push notification.');
      setShowSuccessMessage(true);
    } catch (err) {
      setError('Failed to send flyer. Please try again.');
      console.error('Error sending flyer:', err);
    }
  };

  if (!merchant || merchant.id <= 0) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (flyersPending) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Free Flyers Remaining Display */}
      {merchant.freeFlyersRemaining > 0 && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<LocalOfferIcon />}>
          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
            You have <strong>{merchant.freeFlyersRemaining}</strong> free flyer{merchant.freeFlyersRemaining === 1 ? '' : 's'} remaining!
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Typography variant="h4">
            Create Digital Flyer
          </Typography>
          <Tooltip title="Learn how Digital Flyers work" arrow placement="left">
            <Button
              onClick={() => setShowInfoDialog(true)}
              color="primary"
              variant="outlined"
              startIcon={<InfoIcon />}
              sx={{ fontSize: '1rem', fontWeight: 'medium', px: 2 }}
            >
              How it works
            </Button>
          </Tooltip>
        </div>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          label="Flyer Message"
          value={message}
          onChange={handleMessageChange}
          error={!!error}
          helperText={error || (hasInProgressFlyer ? 'Complete your current flyer before creating a new one' : `${message.length}/100 characters`)}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={generateQuote}
          disabled={message.length === 0 || isLoading || favoriteUsersCount === 0 || hasInProgressFlyer}
        >
          {isLoading ? <CircularProgress size={24} /> : merchant.freeFlyersRemaining > 0 ? 'Create Free Flyer' : 'Generate Quote'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Your Flyers
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Cost (ZAR)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {flyers.map((flyer) => (
                <TableRow key={flyer.id}>
                  <TableCell>{flyer.id}</TableCell>
                  <TableCell>{flyer.message}</TableCell>
                  <TableCell>
                    <Chip
                      label={flyer.status.replaceAll('_', ' ')}
                      color={getStatusChipColor(flyer.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(flyer.date_created).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      R {flyer.cost.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {flyer.status === 'REJECTED' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleEditFlyer(flyer)}
                        sx={{ mr: 1 }}
                      >
                        View Feedback & Edit
                      </Button>
                    )}
                    {flyer.status === 'PENDING' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={async () => await initiatePayfastCheckout({ merchantId: merchant.id, intent: 'digitalFlyer', flyerId: flyer.id })}
                        sx={{ mr: 1 }}
                      >
                        Complete Payment
                      </Button>
                    )}
                    {flyer.status === 'READY_TO_SEND' && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleSendFlyerClick(flyer)}
                        disabled={isSending}
                        startIcon={isSending && flyerToSend?.id === flyer.id ? <CircularProgress size={16} color="inherit" /> : undefined}
                      >
                        {isSending && flyerToSend?.id === flyer.id ? 'Sending...' : 'Send'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Quote Dialog */}
      <Dialog open={showQuoteDialog} onClose={() => setShowQuoteDialog(false)}>
        <DialogTitle>Flyer Quote</DialogTitle>
        <DialogContent>
          {merchant.freeFlyersRemaining > 0 ? (
            <>
              <Typography variant="body1" gutterBottom>
                Great news! This flyer will use one of your free flyers.
              </Typography>
              <Typography variant="h4" sx={{ my: 2, textAlign: 'center', fontWeight: 'bold', color: 'success.main' }}>
                FREE
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You have <strong>{merchant.freeFlyersRemaining}</strong> free flyer{merchant.freeFlyersRemaining === 1 ? '' : 's'} remaining after this one.
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Based on your current number of favorited users, the cost for sending this flyer will be:
              </Typography>
              <Typography variant="h4" sx={{ my: 2, textAlign: 'center', fontWeight: 'bold' }}>
                {displaySymbol} {quote?.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This covers distribution to all your favorited users.
              </Typography>
              {displayCurrency !== 'ZAR' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Prices are defined in South African Rand (ZAR) but displayed in your local currency ({displayCurrency}).
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuoteDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            color={merchant.freeFlyersRemaining > 0 ? "success" : "primary"}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : merchant.freeFlyersRemaining > 0 ? 'Create Free Flyer' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setMessage('');
          setSelectedFlyer(null);
          setError(null);
        }}
      >
        <DialogTitle>Edit Rejected Flyer</DialogTitle>
        <DialogContent>
          {selectedFlyer?.rejection_message && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Rejection Feedback:
              </Typography>
              {selectedFlyer.rejection_message}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please update your message based on the feedback above and resubmit for approval.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label="Flyer Message"
            value={message}
            onChange={handleMessageChange}
            error={!!error}
            helperText={error || `${message.length}/100 characters`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditDialog(false);
              setMessage('');
              setSelectedFlyer(null);
              setError(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateFlyer}
            variant="contained"
            color="primary"
            disabled={isLoading || message === selectedFlyer?.message}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Update & Resubmit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          How Digital Flyers Work
        </DialogTitle>
        <DialogContent>
          <List>
            {merchant.freeFlyersRemaining > 0 && (
              <ListItem>
                <ListItemIcon>
                  <LocalOfferIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Free Flyers Available"
                  secondary={`You currently have ${merchant.freeFlyersRemaining} free flyer${merchant.freeFlyersRemaining === 1 ? '' : 's'} remaining. These can be used without any payment and will be processed the same way as paid flyers.`}
                />
              </ListItem>
            )}
            <ListItem>
              <ListItemIcon>
                <CardMembership color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Target Your Loyal Customers"
                secondary="Digital Flyers are sent as push notifications to all customers who have your card in their BrewPrks wallet, ensuring your message reaches your most engaged customers."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PriceCheck color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Simple Pricing"
                secondary={
                  <React.Fragment>
                    Cost is calculated at <strong>{displaySymbol}{localCurrencyCostPerUser !== null ? localCurrencyCostPerUser.toFixed(2) : '...'}</strong> per customer with your card in their wallet, with a minimum spend of <strong>{displaySymbol}{localCurrencyMinimumCost !== null ? localCurrencyMinimumCost.toFixed(2) : '...'}</strong>. This ensures cost-effective targeting of your loyal customer base.
                    {pricing && displayCurrency !== 'ZAR' ? (
                      <><br />Based on current exchange rates: <strong>R{pricing.flyer_zar_cost_per_user.toFixed(2)}</strong> per customer, minimum <strong>R{pricing.flyer_zar_minimum_cost.toFixed(2)}</strong> in ZAR.</>
                    ) : ''}
                  </React.Fragment>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AdminPanelSettings color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Approval Process"
                secondary="All flyers go through a quick approval process to ensure content quality. If rejected, you'll receive feedback and can edit and resubmit your flyer."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Send color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Instant Delivery"
                secondary="Once approved, your flyer is ready to be sent to all customers with your card in their wallet, providing immediate reach to your target audience."
              />
            </ListItem>
          </List>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, px: 2 }}>
            Note: Payment is required after generating a quote, and the flyer can only be sent after both payment and approval are complete, so allow ample time for this process to get your message out on time. <br />
            Reviews are done manually and can take up to 24 hours.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

             {/* Send Confirmation Dialog */}
       <Dialog
         open={showSendConfirmDialog}
         onClose={() => setShowSendConfirmDialog(false)}
         maxWidth="sm"
         fullWidth
       >
         <DialogTitle>Send Digital Flyer</DialogTitle>
         <DialogContent>
           <Typography variant="body1" gutterBottom sx={{ fontWeight: 'medium' }}>
             Ready to send your flyer?
           </Typography>
           <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
             This will trigger a <strong>push notification</strong> to all customers who have your card saved as a favorite in their BrewPrks wallet.
           </Typography>
           <Typography variant="body2" color="text.secondary">
             Once sent, this action cannot be undone.
           </Typography>
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setShowSendConfirmDialog(false)} disabled={isSending}>Cancel</Button>
           <Button
             onClick={handleConfirmSend}
             variant="contained"
             color="success"
             disabled={isSending}
             startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : undefined}
           >
             {isSending ? 'Sending...' : 'Send Flyer'}
           </Button>
         </DialogActions>
       </Dialog>

       {/* Success Snackbar */}
       <Snackbar
         open={showSuccessMessage}
         autoHideDuration={6000}
         onClose={() => setShowSuccessMessage(false)}
         anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
       >
         <Alert severity="success" onClose={() => setShowSuccessMessage(false)} sx={{ width: '100%' }}>
           {successMessage}
         </Alert>
       </Snackbar>
    </Container>
  );
};

export default DigitalFlyersPage; 